import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Plus, Loader2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';

/**
 * Maßnahmen erfassen, die von Hand im Werbekonto gemacht wurden.
 *
 * Der Monatsbericht speist sein Kapitel „Das haben wir verbessert"
 * ausschließlich aus umgesetzten Empfehlungen. Wer direkt in Google Ads eine
 * Kampagne abschaltet, ein Budget verschiebt oder Suchbegriffe ausschließt,
 * taucht dort nicht auf — der Bericht behauptet dann, es sei nichts passiert.
 *
 * Eine erfasste Maßnahme wird intern wie eine umgesetzte Empfehlung geführt.
 * Damit landet sie automatisch im Monatsbericht, zählt für die
 * Empfehlungsrunden-Ampel und wird nach frühestens 21 Tagen genauso auf
 * Wirkung geprüft. Die Begründung ist Pflicht: Sie ist der Satz, aus dem der
 * Bericht dem Kunden erklärt, warum die Änderung nötig war.
 */
export default function MassnahmeErfassen({
  projectId,
  customerId,
}: {
  projectId: string;
  customerId?: string | null;
}) {
  const queryClient = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [titel, setTitel] = useState('');
  const [grund, setGrund] = useState('');
  const [bereich, setBereich] = useState<'ads' | 'website' | 'technik'>('ads');
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));

  const { data: erfasste } = useQuery({
    queryKey: ['massnahmen-manuell', projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from('seo_recommendations')
        .select('id, title, reason, target, applied_at')
        .eq('project_id', projectId)
        .eq('rec_type', 'manuell')
        .eq('status', 'applied')
        .order('applied_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    staleTime: 30_000,
  });

  const speichern = useMutation({
    mutationFn: async () => {
      // Als umgesetzte Empfehlung ablegen: batch_id ist Pflicht, bekommt aber
      // je Maßnahme eine eigene — sie gehört zu keiner Auswertungsrunde und
      // darf von einer neuen Runde nicht abgelöst werden (die löst nur
      // OFFENE Empfehlungen ab, hier ist der Status bereits 'applied').
      const { error } = await supabase.from('seo_recommendations').insert({
        project_id: projectId,
        customer_id: customerId || null,
        batch_id: crypto.randomUUID(),
        target: bereich,
        rec_type: 'manuell',
        title: titel.trim(),
        reason: grund.trim(),
        proposal: titel.trim(),
        priority: 'mittel',
        status: 'applied',
        applied_at: new Date(`${datum}T12:00:00`).toISOString(),
        model: 'manuell',
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['massnahmen-manuell', projectId] });
      setTitel('');
      setGrund('');
      setOffen(false);
      toast.success('Maßnahme erfasst — sie erscheint im nächsten Monatsbericht');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const loeschen = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('seo_recommendations').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['massnahmen-manuell', projectId] });
      toast.success('Gelöscht');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const absenden = () => {
    if (!titel.trim()) return toast.error('Bitte eintragen, was geändert wurde');
    if (!grund.trim()) return toast.error('Bitte den Grund eintragen — er erklärt dem Kunden im Bericht, warum');
    speichern.mutate();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardCheck className="w-4 h-4 text-gray-400 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900">Von Hand gemachte Maßnahmen</h3>
          <span className="text-[11px] text-gray-400">
            Änderungen direkt im Werbekonto — damit sie im Monatsbericht auftauchen
          </span>
        </div>
        <button
          onClick={() => setOffen((o) => !o)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus className="w-3 h-3" /> {offen ? 'Abbrechen' : 'Maßnahme eintragen'}
        </button>
      </div>

      {offen && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60 space-y-2">
          <div className="grid md:grid-cols-6 gap-2">
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              placeholder={'Was wurde geändert? z. B. Anzeigengruppe „Badsanierung Kosten" pausiert'}
              className="md:col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select
              value={bereich}
              onChange={(e) => setBereich(e.target.value as 'ads' | 'website' | 'technik')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="ads">Google Ads</option>
              <option value="website">Website</option>
              <option value="technik">Technik</option>
            </select>
            <input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              title="Wann die Änderung gemacht wurde — bestimmt, in welchem Monatsbericht sie erscheint"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={2}
            placeholder="Warum? z. B. Die Gruppe holte fast nur Preisrecherche — 47 € in 11 Tagen ohne eine einzige Anfrage."
            title="Aus diesem Satz erklärt der Monatsbericht dem Kunden, warum die Änderung nötig war."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={absenden}
              disabled={speichern.isPending}
              className="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {speichern.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Erfassen
            </button>
            <span className="text-[11px] text-gray-400">
              Zählt wie eine umgesetzte Empfehlung: erscheint im Monatsbericht und wird nach ~3 Wochen auf Wirkung geprüft.
            </span>
          </div>
        </div>
      )}

      {erfasste && erfasste.length > 0 ? (
        <ul className="divide-y divide-gray-50">
          {erfasste.map((m: any) => (
            <li key={m.id} className="px-4 py-2 flex items-start justify-between gap-3 group">
              <div className="min-w-0">
                <p className="text-sm text-gray-800">{m.title}</p>
                {m.reason && <p className="text-[11px] text-gray-500 mt-0.5">{m.reason}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-gray-400 tabular-nums">
                  {m.applied_at ? new Date(m.applied_at).toLocaleDateString('de-DE') : ''}
                </span>
                <button
                  onClick={() => { if (window.confirm(`„${m.title}" löschen?`)) loeschen.mutate(m.id); }}
                  className="p-1 text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !offen && (
          <p className="px-4 py-3 text-xs text-gray-400">
            Noch nichts erfasst. Alles, was über die Empfehlungen umgesetzt wurde, steht ohnehin im Bericht — hier
            kommt nur hinein, was von Hand im Konto passiert ist.
          </p>
        )
      )}
    </div>
  );
}
